import React, { createContext, useContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);

// Allow list configuration (matches API config)
const ALLOWED_DOMAINS = ["novacove.ai"];
const ALLOWED_EMAILS = ["irina.khurana@gmail.com"];

function isEmailAllowed(email) {
  if (!email) return false;

  // Check individual email allow list
  if (ALLOWED_EMAILS.includes(email)) {
    return true;
  }

  // Check domain allow list
  const emailDomain = email.split("@")[1];
  if (emailDomain && ALLOWED_DOMAINS.includes(emailDomain)) {
    return true;
  }

  return false;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem("google_token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Check if token is expired
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          localStorage.removeItem("google_token");
        } else {
          setUser({
            name: decoded.name,
            email: decoded.email,
            picture: decoded.picture,
            sub: decoded.sub,
          });
          setIsAuthenticated(true);
        }
      } catch (err) {
        console.error("Invalid token:", err);
        localStorage.removeItem("google_token");
      }
    }
    setIsLoading(false);
  }, []);

  const login = (credentialResponse) => {
    try {
      const token = credentialResponse.credential;

      const decoded = jwtDecode(token);

      // Check if email is in allow list
      if (!isEmailAllowed(decoded.email)) {
        setIsUnauthorized(true);
        setError("Your email is not authorized to access this system.");
        return false;
      }

      localStorage.setItem("google_token", token);

      const userData = {
        name: decoded.name,
        email: decoded.email,
        picture: decoded.picture,
        sub: decoded.sub,
      };

      setUser(userData);
      setIsAuthenticated(true);
      setIsUnauthorized(false);
      setError(null);
      return true;
    } catch (err) {
      console.error("Failed to decode token:", err);
      setError("Authentication failed. Please try again.");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("google_token");
    setUser(null);
    setIsAuthenticated(false);
    setIsUnauthorized(false);
    setError(null);
  };

  const handleUnauthorized = () => {
    setIsUnauthorized(true);
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem("google_token");
  };

  const getAccessToken = () => {
    return localStorage.getItem("google_token");
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isUnauthorized,
    error,
    login,
    logout,
    getAccessToken,
    handleUnauthorized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
